import { QueryObserverResult, RefetchOptions, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTable } from 'react-table';
import { URLs } from "../../Constants/Consts";
import { ExtendedProjects, JobsFeedbackMessages, JobsMapping } from "../../types/types";
import { fetchApi } from "../Functions";
import { MdOutlineTextRotationAngleup, MdOutlineTextRotationAngledown } from "react-icons/md";
import { setModal } from "../../store/Utilities";
import { useDispatch, useSelector } from "react-redux";
import { FaCloudDownloadAlt, FaEdit, FaMap, FaUsers } from "react-icons/fa";
import { MdBuild } from "react-icons/md";
import { FaBolt } from "react-icons/fa";
import { FaCubes } from "react-icons/fa";
import { toast } from "react-toastify";
import ActionDropdown, { ActionOption } from "../../components/CoreComponents/ActionDropdown";
import { AddProjectForm } from "../../components/AddProjectForm/AddProjectForm";
import { RootState } from "../../store/store";
import roleCheck from "../../utils/roleCheck";
import { ROLES } from "../../utils/roles";

export type TRefetchFn = (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<Array<ExtendedProjects>, Error>>;

export default function useAllProjects() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Get user role for permission checks
    const { role: userRole } = useSelector((state: RootState) => state.SelectedUser.SelectedUser);
    const isSuperAdmin = roleCheck(userRole, [ROLES.SuperAdmin]);

    const { data: extendedProjects, isLoading, refetch } = useQuery<Array<ExtendedProjects>>({
        queryKey: ["ExtendedProjects"],
        queryFn: async () => {
            const projects = await fetchApi(URLs.getExtendedProjects()).then(res => res.json()) as Array<ExtendedProjects>;
            return projects;
        },
    });

    const onClickArchive = useCallback((projectNumber: number, isArchived: boolean) => {
        dispatch(setModal({
            open: true, Chilren: () => <div className="p-4">Are you sure you want to <span className="font-bold">{isArchived ? 'unarchive' : 'archive'}</span> this project ?</div>,
            onConfirm: () => {
                const url = isArchived ? URLs.unArchiveProjectUrl(projectNumber) : URLs.archiveProjectUrl(projectNumber)
                fetchApi(url, { method: "get", }).then((res) => {
                    if (200 === res.status) refetch();
                })
            }
        }))
    }, [dispatch, refetch])

    const onClickEdit = useCallback((projectNumber: number) => {
        dispatch(setModal({
            Chilren: () => <AddProjectForm projectNumber={projectNumber} handleRefetchProjects={refetch} />,
            open: true,
            hideModalFooter: true,
        }));
    }, [dispatch, refetch])

    const onClickPullProjectsAndGenerateCheckList = useCallback((projectNumber: number, jobTypeId: number, jobData: string) => {

        let confirmationMessage: string;
        let successMessage: string;
        let errorMessage: string;

        switch (jobTypeId) {
            case JobsMapping.PullProjectUnits:
                confirmationMessage = JobsFeedbackMessages.projectUnitsPullConfirmation;
                successMessage = JobsFeedbackMessages.projectUnitedPullSuccess;
                errorMessage = JobsFeedbackMessages.projectUnitedPullError;
                break;
            case JobsMapping.LoadRADComponents:
                confirmationMessage = JobsFeedbackMessages.loadRADComponentsConfirmation;
                successMessage = JobsFeedbackMessages.loadRADComponentsSuccess;
                errorMessage = JobsFeedbackMessages.loadRADComponentsError;
                break;
            case JobsMapping.GenerateMechanicalChecklists:
                confirmationMessage = JobsFeedbackMessages.generateMechanicalChecklistsConfirmation;
                successMessage = JobsFeedbackMessages.generateMechanicalChecklistsSuccess;
                errorMessage = JobsFeedbackMessages.generateMechanicalChecklistsError;
                break;
            case JobsMapping.GenerateElectricalChecklists:
                confirmationMessage = JobsFeedbackMessages.generateElectricalChecklistsConfirmation;
                successMessage = JobsFeedbackMessages.generateElectricalChecklistsSuccess;
                errorMessage = JobsFeedbackMessages.generateElectricalChecklistsError;
                break;
            default:
                confirmationMessage = "Perform this action";
                successMessage = "Action completed successfully";
                errorMessage = "Could not complete action";
        }

        const payload = { jobTypeId, projectNumber, jobData };

        dispatch(setModal({
            open: true,
            Chilren: () => <div className="p-4">Are you sure you want to <span className="font-bold">{confirmationMessage}</span> ?</div>,
            onConfirm: async () => {
                try {
                    const res = await fetchApi(
                        URLs.postPullProjectGenerateChecklis(),
                        {
                            method: "post",
                            body: JSON.stringify(payload),
                            headers: { "Content-Type": "application/json" }
                        })
                    if (201 === res.status) {
                        toast.success(successMessage);
                        refetch();
                    } else throw new Error(res.statusText);
                } catch {
                    toast.error(errorMessage);
                }
            }
        }))
    }, [dispatch, refetch])
    const columns = useMemo(() => [
        {
            Header: 'Number',
            accessor: 'projectNumber',
        },
        {
            Header: 'Name',
            accessor: 'projectName',
        },
        {
            Header: 'Location',
            accessor: 'projectLocation',
        },
        {
            Header: 'Country',
            accessor: 'country',
        },
        {
            Header: 'Archived',
            accessor: 'isArchived',
            Cell: ({ value }) => (
                <span>{value ? 'Yes' : 'No'}</span>
            ),
        },
        {
            Header: 'Pending Queue Job',
            accessor: 'pendingQueueJobCount',
        },
        {
            Header: 'Maps',
            accessor: '',
            Cell: ({ row }) => {
                const projectNumber = row.original?.projectNumber;
                const projectDrawingCount = row.original?.projectDrawingCount || 0;
                if (!projectNumber) {
                    return <span>{projectDrawingCount}</span>;
                }
                return (
                    <Link 
                        to={`/ProjectMaps/${projectNumber}`} 
                        rel="noopener noreferrer" 
                        className="hover:text-blue-500 underline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {projectDrawingCount}
                    </Link>
                );
            }
        },
        {
            Header: 'Assignments ',
            accessor: '',
            Cell: ({ row }) => {
                const projectName = row.original?.projectName;
                const projectAssignmentCount = row.original?.projectAssignmentCount || 0;
                if (!projectName) {
                    return <span>{projectAssignmentCount}</span>;
                }
                return (
                    <Link 
                        to={`/ProjectAssignments/${projectName}`} 
                        rel="noopener noreferrer" 
                        className="hover:text-blue-500 underline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {projectAssignmentCount}
                    </Link>
                );
            }
        },
        {
            Header: 'Actions',
            accessor: '',
            Cell: ({ row }) => {
                const { projectNumber, isArchived, regionId, pendingQueueJobCount, projectName, sapUnitCount, sapTaskCount, radComponentCount } = row.original || {};
                
                // Load SAP Units: Region 1, no units loaded, no pending jobs, no mechanical tasks
                const isPullBtnAvailable = regionId === 1 && sapUnitCount === 0 && pendingQueueJobCount === 0 && sapTaskCount === 0
                
                // Generate Mechanical Checklist: Region 1, units loaded, no pending jobs, no mechanical tasks
                const isGenerateMechanicalAvailable = regionId === 1 && sapUnitCount > 0 && pendingQueueJobCount === 0 && sapTaskCount === 0
                
                // Load RAD Components: Region 1, no RAD components, no pending jobs, no electrical tasks
                const isLoadRADAvailable = regionId === 1 && pendingQueueJobCount === 0
                
                // Generate Electrical Checklist: Region 1, RAD components loaded, no pending jobs, no electrical tasks
                const isGenerateElectricalAvailable = regionId === 1 && radComponentCount > 0 && pendingQueueJobCount === 0
                
                const actionOptions: ActionOption[] = [
                    {
                        id: 'edit',
                        label: 'Edit',
                        icon: <FaEdit size={20} />,
                        onClick: () => onClickEdit(projectNumber),
                        condition: isSuperAdmin,
                        immediate: true
                    },
                    {
                        id: 'maps',
                        label: 'Maps',
                        icon: <FaMap size={20} />,
                        onClick: () => navigate(`/ProjectMaps/${projectNumber}`),
                        condition: true,
                        immediate: true
                    },
                    {
                        id: 'assignments',
                        label: 'Assignments',
                        icon: <FaUsers size={20} />,
                        onClick: () => navigate(`/ProjectAssignments/${projectName}`),
                        condition: true,
                        immediate: true
                    },
                    {
                        id: 'archive',
                        label: isArchived ? 'Unarchive' : 'Archive',
                        icon: isArchived ? 
                            <MdOutlineTextRotationAngledown size={20} style={{ color: 'red' }} /> : 
                            <MdOutlineTextRotationAngleup size={20} style={{ color: 'green' }} />,
                        onClick: () => onClickArchive(projectNumber, isArchived),
                        condition: isSuperAdmin
                    },
                    {
                        id: 'pull-units',
                        label: 'Load SAP Units',
                        icon: <FaCloudDownloadAlt size={20} />,
                        onClick: () => onClickPullProjectsAndGenerateCheckList(projectNumber, JobsMapping.PullProjectUnits, projectName),
                        condition: isSuperAdmin,
                        disabled: !isPullBtnAvailable
                    },
                    {
                        id: 'generate-checklist',
                        label: 'Generate Mechanical Checklist Tasks',
                        icon: <MdBuild size={20} />,
                        onClick: () => onClickPullProjectsAndGenerateCheckList(projectNumber, JobsMapping.GenerateMechanicalChecklists, projectName),
                        condition: isSuperAdmin,
                        disabled: !isGenerateMechanicalAvailable
                    },
                    {
                        id: 'load-rad',
                        label: 'Load RAD Components',
                        icon: <FaCubes size={20} />,
                        onClick: () => onClickPullProjectsAndGenerateCheckList(projectNumber, JobsMapping.LoadRADComponents, projectName),
                        condition: isSuperAdmin,
                        disabled: !isLoadRADAvailable
                    },
                    {
                        id: 'generate-electrical',
                        label: 'Generate Electrical Checklist Tasks',
                        icon: <FaBolt size={20} />,
                        onClick: () => onClickPullProjectsAndGenerateCheckList(projectNumber, JobsMapping.GenerateElectricalChecklists, projectName),
                        condition: isSuperAdmin,
                        disabled: !isGenerateElectricalAvailable
                    }
                ];

                return <ActionDropdown options={actionOptions} />;
            }
        }
    ], [navigate, onClickArchive, onClickEdit, onClickPullProjectsAndGenerateCheckList]);

    const data = useMemo(() => extendedProjects || [], [extendedProjects]);

    return { ...useTable({ columns, data }), isLoading, handleRefetchProjects: refetch };
}
